import React, { useCallback, useState } from "react"
import { useDispatch } from "react-redux"
import constants from "../../Constants"
import fetch from "../../Utils/fetch"

/**
 * Build path for PYP SF sync admin API.
 * Production uses REACT_APP_API_URL ending in /v1; local dev often uses http://localhost:3333 without /v1.
 */
function pypSfSyncPath(suffix) {
  const host = String(constants.HOST || "").replace(/\/+$/, "")
  const needsV1 = !host.endsWith("/v1")
  return `${needsV1 ? "/v1" : ""}/admin/pypSfSync/${suffix}`
}

function formatOutput(data) {
  if (data === undefined || data === null) return ""
  if (typeof data === "string") return data
  try {
    return JSON.stringify(data, null, 2)
  } catch {
    return String(data)
  }
}

export default function PypSfSyncAdmin() {
  const dispatch = useDispatch()
  const [output, setOutput] = useState("")
  const [loading, setLoading] = useState(false)
  const [scanLimit, setScanLimit] = useState(500)
  const [scanOffset, setScanOffset] = useState(0)
  const [syncLimit, setSyncLimit] = useState(1)
  const [queueStatus, setQueueStatus] = useState("pending")

  const run = useCallback(
    async (fn) => {
      setLoading(true)
      setOutput("")
      try {
        const res = await fn()
        setOutput(formatOutput(res?.data))
      } catch (e) {
        setOutput(
          formatOutput({
            error: e?.message || String(e),
            status: e?.response?.status,
            body: e?.response?.data
          })
        )
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const handleScan = () =>
    run(() =>
      dispatch(
        fetch(pypSfSyncPath("scan"), {
          type: "POST",
          data: {
            requestPayload: {
              limit: Number(scanLimit) || 500,
              offset: Number(scanOffset) || 0
            }
          }
        })
      )
    )

  const handleQueue = () => {
    const params = {}
    if (queueStatus && queueStatus !== "all") {
      params.status = queueStatus
    }
    return run(() =>
      dispatch(
        fetch(pypSfSyncPath("queue"), {
          type: "GET",
          data: { params }
        })
      )
    )
  }

  const handleSync = () =>
    run(() =>
      dispatch(
        fetch(pypSfSyncPath("sync"), {
          type: "POST",
          data: {
            requestPayload: {
              limit: Number(syncLimit) || 1
            }
          }
        })
      )
    )

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 8 }}>PYP → Salesforce sync queue</h1>
      <p style={{ color: "#555", marginBottom: 24 }}>
        Admin only. API base: <code>{constants.HOST}</code>
        <br />
        These buttons call the same routes as the backend (
        <code>/admin/pypSfSync/…</code> with <code>/v1</code> added when the base URL does not
        already include it).
      </p>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18 }}>Scan (POST)</h2>
        <label style={{ marginRight: 12 }}>
          limit{" "}
          <input
            type="number"
            min={1}
            value={scanLimit}
            onChange={(e) => setScanLimit(e.target.value)}
            style={{ width: 80 }}
          />
        </label>
        <label style={{ marginRight: 12 }}>
          offset{" "}
          <input
            type="number"
            min={0}
            value={scanOffset}
            onChange={(e) => setScanOffset(e.target.value)}
            style={{ width: 80 }}
          />
        </label>
        <button type="button" onClick={handleScan} disabled={loading}>
          Run scan
        </button>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18 }}>Queue (GET)</h2>
        <label style={{ marginRight: 12 }}>
          status{" "}
          <select
            value={queueStatus}
            onChange={(e) => setQueueStatus(e.target.value)}
          >
            <option value="all">all</option>
            <option value="pending">pending</option>
            <option value="synced">synced</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <button type="button" onClick={handleQueue} disabled={loading}>
          Load queue
        </button>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18 }}>Sync (POST)</h2>
        <label style={{ marginRight: 12 }}>
          limit (batch size){" "}
          <input
            type="number"
            min={1}
            value={syncLimit}
            onChange={(e) => setSyncLimit(e.target.value)}
            style={{ width: 80 }}
          />
        </label>
        <button type="button" onClick={handleSync} disabled={loading}>
          Process pending
        </button>
      </section>

      {loading && <p>Loading…</p>}

      <pre
        style={{
          background: "#f5f5f5",
          padding: 16,
          overflow: "auto",
          maxHeight: "60vh",
          fontSize: 13,
          border: "1px solid #ddd",
          borderRadius: 4
        }}
      >
        {output || "(response will appear here)"}
      </pre>
    </div>
  )
}
